import { CheckOutlined, EyeOutlined, FilePdfOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Form, Input, Modal, notification, Row, Typography } from "antd";
import {
  IOU_COST_DELETE_ENDPOINT,
  IOU_COST_LIST_ENDPOINT,
  IOU_COST_PDF_ENDPOINT,
  IOU_DISBURSE_ENDPOINT,
  IOU_REQUESTED_BY_USER_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IOU_DISBURSE_PERMISSION,
  SALES_CONTRACT_IOU_COST_CREATE_PERMISSION,
  SALES_CONTRACT_IOU_COST_VIEW_PERMISSION,
} from "routes/permissions";
import { cglFormatDate, hasPermission, toFixedAndComma } from "utils/functions";
import { checkPermission } from "./config/checkPermission";
import { ROLES } from "./constants/roles";

export default function IOUDisbursement() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);

  const loggedInUserRole = localStorage.getItem("user_type");

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [disburseModalVisible, setDisburseModalVisible] = useState(false);
  const [disburseRecord, setDisburseRecord] = useState(null);
  const [receivedBy, setReceivedBy] = useState("");
  const [disburseForm] = Form.useForm();

  const { Title } = Typography;

  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllIOUCosts = useCallback(
    async (filterValues) => {
      const { iou_number, status } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${IOU_COST_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(iou_number && { iou_number }),
        // ...(status && { status }),
        //status:["approved", "disbursed"],
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];

        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `iou-cost-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
      }
    },

    [currentPage],
  );

  useEffect(() => {
    getAllIOUCosts(filterData);
  }, [refresh, filterData, getAllIOUCosts]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedIOUCost = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this IOU Disbursement?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(`${IOU_COST_DELETE_ENDPOINT}/${id}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "IOU Disbursement deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/iou-disbursement/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-disbursement/edit/${record.id}`);
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-disbursement/view/${record.id}`);
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    const allSameStatus =
      newSelectedRows.length > 0 && newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (allSameStatus) {
      const canRequestStatus = newSelectedRows.some((row) =>
        checkPermission(loggedInUserRole, row.status, loggedInUserId === row?.user?.id, "request"),
      );

      const canApproveStatus = newSelectedRows.some((row) =>
        checkPermission(loggedInUserRole, row.status, loggedInUserId === row?.user?.id, "approve"),
      );

      const canRejectStatus = newSelectedRows.some((row) =>
        checkPermission(loggedInUserRole, row.status, loggedInUserId === row?.user?.id, "reject"),
      );

      setCanRequest(canRequestStatus);
      setCanApprove(canApproveStatus);
      setCanReject(canRejectStatus);
    } else {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !["created", "requested", "by_department", "rejected"].includes(record.status),
    }),
  };

  const handleRequestAction = async (val) => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No rows selected for the request",
        placement: "bottomRight",
      });
      return;
    }

    Modal.confirm({
      title: "Confirm Request",
      content: "Are you sure you want to submit the selected IOU Costs?",
      okText: "Yes, Submit",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true); // Set loading state to true
          const requestedIds = selectedRows.map((row) => row.id);
          const formData = new FormData();
          formData.append("requested_id", JSON.stringify(requestedIds));
          formData.append("status", val);

          const response = await postData(IOU_REQUESTED_BY_USER_ENDPOINT, formData);

          if (response && (response?.code === 200 || response?.status === 200)) {
            alertPop("success", response.message || "Request submitted successfully");

            setSelectedRowKeys([]);
            setSelectedRows([]);

            refetch(); // Refresh the list by calling the list API
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to submit IOU Disbursement form",
            placement: "bottomRight",
          });
        } finally {
          setLoading(false); // Reset loading state
        }
      },
    });
  };

  const handleDisburseAction = (record) => {
    setDisburseRecord(record);
    setDisburseModalVisible(true);
  };

  const submitDisbursement = async () => {
    if (!receivedBy.trim()) {
      notification.warning({
        message: "Warning",
        description: "Please provide the 'Taken By' field.",
        placement: "topRight",
      });
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("iou_id", disburseRecord.id);
      formData.append("receive_by", receivedBy);

      const response = await postData(IOU_DISBURSE_ENDPOINT, formData);

      if (response && (response?.code === 200 || response?.status === 200)) {
        alertPop("success", response.message || "IOU Disbursement completed successfully");

        refetch(); // Refresh the table data
        setDisburseModalVisible(false);
        setReceivedBy("");
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to disburse IOU",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAction = async (record) => {
    try {
      setLoading(true);

      const response = await getData(
        `${IOU_COST_PDF_ENDPOINT}/${record.id}`,
        false,
        {},
        { "Content-Type": "application/pdf" },
        "blob",
      );

      if (response?.status === 200) {
        const outputFilename = `iou_report_${record.iou_number}.pdf`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response?.data], {
            type: "application/pdf",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
      } else {
        HandleNotification("error", "bottomRight", "Error getting PDF report!", null);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to disburse IOU",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "IOU Number",
        dataIndex: "iou_number",
        width: "15%",
        key: "iou_number",
      },
      {
        title: "IOU For",
        dataIndex: "iou_for",
        width: "15%",
        key: "iou_for",
      },
      {
        title: "Total Amount",
        dataIndex: "iou_amount",
        width: "15%",
        key: "iou_amount",
        render: (text) => toFixedAndComma(text || 0),
      },
      {
        title: "Taken By",
        dataIndex: ["receive_by"],
        width: "15%",
        key: "taken_by",
      },

      {
        title: "Disbursement Date",
        dataIndex: "disburse_date",
        width: "15%",
        key: "disburse_date",
        render: (text) => (text ? cglFormatDate(text) : "-"),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: "15%",
        key: "status",

        render: (status) => {
          let color = "blue";
          let text = status || "N/A";

          if (status === "requested") {
            color = "orange";
            text = "Requested";
          } else if (status === "approved") {
            color = "green";
            text = "Approved";
          } else if (status === "rejected") {
            color = "red";
            text = "Rejected";
          } else if (status === "by_department") {
            color = "purple";
            text = "By Department";
          } else if (status === "by_director") {
            color = "gold";
            text = "By Director";
          }

          return (
            <span
              style={{
                color: "#fff",
                backgroundColor: color,
                padding: "4px 8px",
                borderRadius: "4px",
                textTransform: "capitalize",
              }}
            >
              {text}
            </span>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => {
          const menuItems = [
            {
              key: "view",
              label: "View",
              icon: <EyeOutlined />,
              onClick: () => navigateToView(record),
              disabled: !hasPermission([IOU_DISBURSE_PERMISSION]),
            },
            ...(record.disburse_status !== "approved"
              ? [
                  {
                    key: "disburse",
                    label: "Disburse",
                    icon: <CheckOutlined />,
                    onClick: () => handleDisburseAction(record),
                    disabled:
                      !hasPermission([IOU_DISBURSE_PERMISSION]) ||
                      [ROLES.DEPARTMENT_HEAD, ROLES.DIRECTOR, ROLES.ACCOUNTS].includes(loggedInUserRole),
                  },
                ]
              : []),
            {
              key: "Download",
              label: "Download",
              icon: <FilePdfOutlined />,
              onClick: () => handleDownloadAction(record),
              disabled: !hasPermission([IOU_DISBURSE_PERMISSION]),
            },
          ];

          return (
            <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["hover"]}>
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [navigateToView, handleDisburseAction],
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllIOUCosts}
        filterName="iou_cost"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <div style={{ display: "flex", alignItems: "center" }}>
                <ExtraContent
                  refresh={refresh}
                  setRefresh={setRefresh}
                  handleAddItemOpen={navigateToAdd}
                  disabled={!hasPermission([SALES_CONTRACT_IOU_COST_CREATE_PERMISSION])}
                  hideAddButton={true}
                  addButtonText={
                    // hasPermission([SALES_CONTRACT_IOU_COST_CREATE_PERMISSION])
                    //   ? "Add New"
                    //   : "No Permission"
                    null
                  }
                  showFilter={showFilter}
                  setShowFilter={setShowFilter}
                />
              </div>
            }
            title={<Title level={5}>IOU Disbursement</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 2 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllIOUCosts(filterData)}
                dataSource={tableData}
                columns={tableColumns}
                // rowSelection={rowSelection}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>

            <Modal
              title="Disbursement Confirmation"
              visible={disburseModalVisible}
              onCancel={() => {
                setDisburseModalVisible(false);
                disburseForm.resetFields(); // Reset the form fields on cancel
              }}
              onOk={() => {
                disburseForm.submit(); // Trigger form submission
                getAllIOUCosts();
              }}
              okText="Disburse"
              cancelText="Cancel"
            >
              <Form
                form={disburseForm}
                layout="vertical"
                onFinish={async (values) => {
                  try {
                    setLoading(true);

                    const formData = new FormData();
                    formData.append("iou_id", disburseRecord.id);
                    formData.append("receive_by", values.receive_by);

                    const response = await postData(IOU_DISBURSE_ENDPOINT, formData);

                    if (response && (response?.code === 200 || response?.status === 200)) {
                      alertPop("success", response.message || "IOU Disbursement completed successfully");

                      refetch(); // Refresh the table data
                      setDisburseModalVisible(false);
                      disburseForm.resetFields(); // Reset the form fields after successful submission
                    }
                  } catch (error) {
                    notification.error({
                      message: "Error",
                      description: "Failed to disburse IOU",
                      placement: "topRight",
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <Form.Item label="IOU Number">
                  <Input value={disburseRecord?.iou_number} disabled />
                </Form.Item>
                <Form.Item label="IOU For">
                  <Input value={disburseRecord?.iou_for} disabled />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="RA">
                      <Input value={toFixedAndComma(disburseRecord?.ra_amount)} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="NRA">
                      <Input value={toFixedAndComma(disburseRecord?.nra_amount)} disabled />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label="IOU Amount">
                  <Input value={toFixedAndComma(disburseRecord?.iou_amount)} disabled />
                </Form.Item>
                <Form.Item
                  label="Taken By"
                  name="receive_by"
                  rules={[{ required: true, message: "Please enter the name of the person taking the IOU" }]}
                >
                  <Input placeholder="Taken By" />
                </Form.Item>
              </Form>
            </Modal>
          </>
        }
      />
    </>
  );
}
